"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type {
  AddItemPayload,
  CartLine,
  CartServiceability,
  CartV3,
  CheckoutPayload,
  CheckoutResult,
  VerifyCartResult,
} from "@/src/types/cartV3";
import * as cartApi from "@/src/services/cartV3.service";
import { getServiceArea, isDeliveryBlocked, loadServiceArea } from "@/src/constant/serviceArea/serviceArea";

/** Channel identifier persisted on the cart (and then the order) so backend analytics know it came from seller-web. */
const ORDER_PLACED_FROM = "web-orders";

/**
 * Outlets with a restricted delivery area refuse items while the buyer's
 * selected address is outside it. Throwing here surfaces the reason through
 * the existing toast handling at every add-to-cart call site.
 */
async function assertServiceable(outletId?: string) {
  // A product from a store whose page was never opened (search, home rails)
  // has no area fetched yet; ask before judging.
  await loadServiceArea(outletId);
  if (!isDeliveryBlocked(outletId)) return;
  throw new Error(getServiceArea(outletId)!.blockMessage);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Compat shape so existing consumers (Header, FloatingCart, CartDrawer,
 * AllProducts, ProductDetailClient, etc.) keep working with minimal changes.
 * `productId` is the per-line lookup key — for combo lines it's synthesised
 * as `combo:${comboId}` so callers can keep using the existing productId-keyed
 * helpers (getItemQuantity, updateQuantity, removeItem).
 * ────────────────────────────────────────────────────────────────────────── */
export interface CartItemView {
  productId: string;
  variantId?: string;
  /** Human label for the chosen variant, e.g. "EU 27". Absent for non-variant lines. */
  variantLabel?: string;
  type?: 'product' | 'combo';
  comboId?: string;
  comboItems?: Array<{
    productId: string;
    name: string;
    quantity: number;
    sku: string;
    thumbnail?: string;
  }>;
  outletId: string;
  tenantId: string;
  outletName?: string;
  outletLogoUrl?: string;
  brandName?: string;
  name: string;
  price: number;
  mrp?: number;
  quantity: number;
  image?: string;
  lineId: string;
}

export interface AddItemInput {
  productId: string;
  outletId: string;
  tenantId?: string;
  variantId?: string;
  name: string;
  price: number;
  mrp?: number;
  image?: string;
  quantity: number;
}

export interface AddComboInput {
  comboId: string;
  outletId: string;
  tenantId?: string;
  quantity: number;
}

export const comboLookupKey = (comboId: string): string => `combo:${comboId}`;

interface CartContextValue {
  cart: CartV3 | null;
  loading: boolean;
  syncing: boolean;
  items: CartItemView[];
  /**
   * Set when the selected address cannot receive part of the cart. Carries the
   * exact lines, so the drawer and the checkout screen can name them and offer
   * removal rather than dead-ending the buyer at payment.
   */
  serviceability: CartServiceability | null;
  /** Removes every line the current address can't receive, in one go. */
  removeUnserviceableItems: () => Promise<void>;

  addItem: (input: AddItemInput) => Promise<void>;
  addCombo: (input: AddComboInput) => Promise<void>;
  removeItem: (productId: string, variantId?: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;

  /** Apply a coupon to a specific outlet. One coupon per outlet; re-applying replaces. */
  applyCoupon: (outletId: string | null, code: string) => Promise<void>;
  /** Remove the coupon at a specific outlet. */
  removeCoupon: (outletId?: string | null) => Promise<void>;
  updateAddress: (addressId: string | number) => Promise<void>;

  verifyCart: () => Promise<VerifyCartResult>;
  checkout: (payload?: CheckoutPayload) => Promise<CheckoutResult>;

  getTotal: () => number;
  getItemCount: () => number;
  getItemQuantity: (productId: string, variantId?: string) => number;

  /** @deprecated kept for backwards-compat. v3 backend already holds the cart. */
  syncToBackend: (userId: number, outletId: string) => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

function toItemView(line: CartLine): CartItemView {
  const isCombo = line.type === 'combo' && line.comboId;
  return {
    productId: isCombo ? comboLookupKey(line.comboId as string) : (line.productId as string),
    variantId: line.variantId,
    variantLabel: line.snapshot.variantLabel,
    type: line.type,
    comboId: line.comboId,
    comboItems: isCombo
      ? (line.comboItems || []).map((ci) => ({
          productId: ci.productId,
          name: ci.name,
          quantity: ci.quantity,
          sku: ci.sku,
          thumbnail: ci.thumbnail,
        }))
      : undefined,
    outletId: line.outletId,
    tenantId: line.tenantId,
    outletName: line.snapshot.outletName,
    outletLogoUrl: line.snapshot.outletLogoUrl,
    brandName: line.snapshot.brandName,
    name: line.snapshot.name,
    price: line.pricing.unitPrice,
    mrp: line.snapshot.mrp,
    quantity: line.quantity,
    image: line.snapshot.thumbnail,
    lineId: line.lineId,
  };
}

function findLineByProductId(cart: CartV3 | null, productId: string, variantId?: string): CartLine | undefined {
  if (!cart) return undefined;
  // Combo lookup keys take the form `combo:${comboId}`.
  if (productId.startsWith('combo:')) {
    const comboId = productId.slice('combo:'.length);
    return cart.items.find((l) => l.type === 'combo' && l.comboId === comboId);
  }
  return cart.items.find(
    (l) => (l.type ?? 'product') === 'product' && l.productId === productId && (l.variantId || '') === (variantId || '')
  );
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartV3 | null>(null);
  const [serviceability, setServiceability] = useState<CartServiceability | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const initRef = useRef(false);

  const fetchCart = useCallback(async () => {
    try {
      const { cart: fresh, serviceability: sv } = await cartApi.getCart();
      setCart(fresh);
      setServiceability(sv ?? null);
    } catch (err) {
      console.warn("[cart] failed to load cart:", (err as Error).message);
      setCart(null);
      setServiceability(null);
    }
  }, []);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      setLoading(true);
      await fetchCart();
      setLoading(false);
    })();
  }, [fetchCart]);

  const addItem = useCallback(
    async (input: AddItemInput) => {
      if (!input.tenantId) {
        console.warn("[cart] addItem missing tenantId; ignoring", input);
        return;
      }
      await assertServiceable(input.outletId);
      const payload: AddItemPayload = {
        productId: input.productId,
        variantId: input.variantId,
        outletId: input.outletId,
        tenantId: input.tenantId,
        quantity: input.quantity,
        orderPlacedfrom: ORDER_PLACED_FROM,
      };
      setSyncing(true);
      try {
        const fresh = await cartApi.addItem(payload);
        setCart(fresh);
      } finally {
        setSyncing(false);
      }
    },
    []
  );

  const addCombo = useCallback(
    async (input: AddComboInput) => {
      if (!input.tenantId) {
        console.warn("[cart] addCombo missing tenantId; ignoring", input);
        return;
      }
      await assertServiceable(input.outletId);
      const payload: AddItemPayload = {
        comboId: input.comboId,
        outletId: input.outletId,
        tenantId: input.tenantId,
        quantity: input.quantity,
        orderPlacedfrom: ORDER_PLACED_FROM,
      };
      setSyncing(true);
      try {
        const fresh = await cartApi.addItem(payload);
        setCart(fresh);
      } finally {
        setSyncing(false);
      }
    },
    []
  );

  const removeItem = useCallback(
    async (productId: string, variantId?: string) => {
      const line = findLineByProductId(cart, productId, variantId);
      if (!line) return;
      setSyncing(true);
      try {
        const fresh = await cartApi.removeItem(line.lineId);
        setCart(fresh);
        // Removing a blocked line can clear the banner; only GET reports it.
        if (serviceability) await fetchCart();
      } finally {
        setSyncing(false);
      }
    },
    [cart, serviceability, fetchCart]
  );

  /**
   * Drops every line the selected address cannot receive. Sequential, not
   * parallel: each removal returns a whole new cart, and concurrent writes to
   * the same Redis key would have the last response win and resurrect a line
   * another call had already removed.
   */
  const removeUnserviceableItems = useCallback(async () => {
    const lineIds = serviceability?.items?.map((i) => i.lineId) ?? [];
    if (!lineIds.length) return;
    setSyncing(true);
    try {
      for (const lineId of lineIds) {
        try {
          await cartApi.removeItem(lineId);
        } catch (err) {
          // A line already gone (buyer removed it in another tab) must not stop
          // the rest — the point of this call is to leave a deliverable cart.
          console.warn("[cart] could not remove line", lineId, (err as Error).message);
        }
      }
    } finally {
      await fetchCart();
      setSyncing(false);
    }
  }, [serviceability, fetchCart]);

  const updateQuantity = useCallback(
    async (productId: string, quantity: number, variantId?: string) => {
      const line = findLineByProductId(cart, productId, variantId);
      if (!line) return;
      if (quantity <= 0) {
        setSyncing(true);
        try {
          const fresh = await cartApi.removeItem(line.lineId);
          setCart(fresh);
        } finally {
          setSyncing(false);
        }
        return;
      }
      setSyncing(true);
      try {
        const fresh = await cartApi.updateQuantity(line.lineId, quantity);
        setCart(fresh);
      } finally {
        setSyncing(false);
      }
    },
    [cart]
  );

  const clearCart = useCallback(async () => {
    setSyncing(true);
    try {
      const fresh = await cartApi.clearCart();
      setCart(fresh);
    } finally {
      setSyncing(false);
    }
  }, []);

  const applyCoupon = useCallback(async (outletId: string | null, code: string) => {
    setSyncing(true);
    try {
      const fresh = await cartApi.applyCoupon(outletId, code);
      setCart(fresh);
    } finally {
      setSyncing(false);
    }
  }, []);

  const removeCoupon = useCallback(async (outletId?: string | null) => {
    setSyncing(true);
    try {
      const fresh = await cartApi.applyCoupon(outletId ?? null, null);
      setCart(fresh);
    } finally {
      setSyncing(false);
    }
  }, []);

  const updateAddress = useCallback(
    async (addressId: string | number) => {
      setSyncing(true);
      try {
        const fresh = await cartApi.updateAddress(String(addressId));
        setCart(fresh);
        // The address is the one input that decides serviceability, and only
        // GET /cart reports it — so re-read rather than leave a stale banner
        // (or a stale absence of one) on screen.
        await fetchCart();
      } catch (err) {
        // A refused address leaves the cart on its previous one. Re-read so the
        // UI reflects what the server actually kept, then rethrow for the caller
        // to show the message and offer removal.
        if (cartApi.isLocationError(err)) await fetchCart();
        throw err;
      } finally {
        setSyncing(false);
      }
    },
    [fetchCart]
  );

  const verifyCart = useCallback(async () => {
    return cartApi.verifyCart();
  }, []);

  const checkout = useCallback(async (payload: CheckoutPayload = {}) => {
    return cartApi.checkoutCart(payload);
  }, []);

  const items = useMemo<CartItemView[]>(() => {
    if (!cart) return [];
    return cart.items.map(toItemView);
  }, [cart]);

  const getTotal = useCallback(() => {
    if (cart?.totals) return cart.totals.grandTotal;
    return items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  }, [cart, items]);

  const getItemCount = useCallback(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const getItemQuantity = useCallback(
    (productId: string, variantId?: string) =>
      items.find(
        (i) => i.productId === productId && (i.variantId || "") === (variantId || "")
      )?.quantity ?? 0,
    [items]
  );

  const syncToBackend = useCallback(async () => {
    // v3 cart is already on the backend (Redis-backed session). No client-side sync needed.
    await fetchCart();
  }, [fetchCart]);

  const value: CartContextValue = {
    cart,
    loading,
    syncing,
    items,
    serviceability,
    removeUnserviceableItems,
    addItem,
    addCombo,
    removeItem,
    updateQuantity,
    clearCart,
    refreshCart: fetchCart,
    applyCoupon,
    removeCoupon,
    updateAddress,
    verifyCart,
    checkout,
    getTotal,
    getItemCount,
    getItemQuantity,
    syncToBackend,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
